import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Question, questionApi } from "./api";
type Terms = {
  title: string;
  text: string;
  version: string;
  hash: string;
  testOnly: boolean;
};
type Receipt = {
  termsText: string;
  termsVersion: string;
  acceptedAt: string;
  testOnly: boolean;
};
export default function QuestionEngagement({
  question,
  dirty,
  onActivated,
}: {
  question: Question;
  dirty: boolean;
  onActivated: () => Promise<void>;
}) {
  const [terms, setTerms] = useState<Terms | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setAccepted(false);
    setError("");
    questionApi<{ terms: Terms | null; receipt: Receipt | null }>(
      `/${question.id}/engagement`,
      "GET",
      undefined,
      controller.signal
    )
      .then(data => {
        setTerms(data.terms);
        setReceipt(data.receipt);
      })
      .catch(e => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [question.id, question.version]);
  async function activate() {
    if (!terms || !accepted || dirty || busy) return;
    setBusy(true);
    setError("");
    try {
      await questionApi(`/${question.id}/activate`, "POST", {
        version: question.version,
        termsVersion: terms.version,
        termsHash: terms.hash,
        accepted: true,
      });
      await onActivated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <p role="status">Loading notice…</p>;
  return (
    <section className="question-engagement" aria-label="Engagement setup">
      {error && <Alert variant="danger">{error}</Alert>}
      {receipt ? (
        <>
          <p>{receipt.termsText}</p>
          <small title={receipt.termsVersion}>
            You accepted these terms on{" "}
            {new Date(receipt.acceptedAt).toLocaleString()}.
          </small>
        </>
      ) : terms ? (
        <>
          <h2>{terms.title}</h2>
          <p>{terms.text}</p>
          <Form.Check
            id="question-acceptance"
            checked={accepted}
            disabled={busy || dirty}
            onChange={e => setAccepted(e.target.checked)}
            label="I have read and accept this test-beta notice."
          />
          {dirty && <p>Save your changes before continuing.</p>}
          <Button
            className="mt-3"
            disabled={!accepted || busy || dirty}
            onClick={activate}
          >
            {busy ? "Opening…" : "Continue to AI conversation"}
          </Button>
        </>
      ) : (
        <p>Engagement setup is not open yet. Your draft is saved.</p>
      )}
    </section>
  );
}
